import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import makeApi, { URLS } from "Utils/apiURL";
import SparkleIcon from "Components/common/SparkleIcon";
import { dataSearchState } from "./state";
import { savedSearchStateToListBuilderImport } from "../agents/icpListBuilder";
import { getCurrentSearchState } from "./searchState";
// Same look as Agent Studio's "Describe it" console (ag-hero / ag-console / ag-genbuild / ag-chip).
import "../agents/agents.scss";

const MAX_LENGTH = 500;

const EXAMPLES = {
  contacts: [
    "VP Sales at fintech companies in New York with 50-500 employees",
    "CTOs at Series B SaaS startups in Germany",
    "HR directors at hospitals in Texas using Workday",
    "Spanish-speaking customer success managers in the US",
    "Founders of companies founded in the last 3 years that raised $10M+"
  ],
  companies: [
    "Public companies in the UK that make payroll software with 1,000+ employees",
    "SaaS companies in Germany that raised a Series B",
    "Hospitals in Texas using Workday",
    "Fintech companies in New York with 50-500 employees"
  ]
};

// Cycled under the orb while the filters are being built, so the wait feels alive.
const BUILD_STEPS = [
  "Reading your description…",
  "Matching titles and seniority…",
  "Finding industries and locations…",
  "Setting your filters…"
];

const isCompaniesTab = () => dataSearchState.activeSearchTab === "companies";

/** Apply List Builder saved-search state as the current filters, on the given tab, and run that tab's search. */
const applyState = async (state, companies) => {
  const { filters, selected } = savedSearchStateToListBuilderImport(state, companies);
  await dataSearchState.applyFiltersFromCompanyExtensionImport(filters, selected, { skipPeopleSearch: companies });
  if (companies) {
    await dataSearchState.searchCompanyData();
  }
};

/**
 * Turn a description into filters — or, with {refine: true}, change the current filters from an instruction —
 * then apply them on the current tab and record the result note (with the previous filters for undo).
 * Returns an error message, or null on success.
 */
const runAiSearch = async (text, { refine = false } = {}) => {
  dataSearchState.setAiSearchRunning(true);
  try {
    return await buildAndApply(text, refine);
  } finally {
    dataSearchState.setAiSearchRunning(false);
  }
};

const buildAndApply = async (text, refine) => {
  const companies = isCompaniesTab();
  dataSearchState.setAiSearchNote(null);
  const previous = {
    filterComponentData: JSON.parse(JSON.stringify(dataSearchState.filterComponentData || {})),
    selectedFilteredList: JSON.parse(JSON.stringify(dataSearchState.selectedFilteredList || {}))
  };
  const res = await makeApi({
    url: URLS.listBuilderAiSearch,
    method: "POST",
    data: {
      query: text,
      tab: companies ? "companies" : "contacts",
      ...(refine ? { mode: "refine", state: getCurrentSearchState() } : {})
    }
  });
  const data = res?.data;
  if (!data?.state) {
    // Our endpoint answers {error: "<message for the user>"}; anything else (framework errors carry a
    // timestamp/status) gets the generic message rather than e.g. "Not Found".
    const body = res?.response?.data;
    const message = body && typeof body.error === "string" && !body.timestamp ? body.error : null;
    return message || "Couldn't build filters right now. Please try again.";
  }
  const skipped = [...(data.unsupported || []), ...(data.unresolved || []).map(u => u?.value).filter(Boolean)];
  await applyState(data.state, companies);
  dataSearchState.setAiSearchNote({
    query: text,
    summary: data.summary || [],
    changes: refine ? data.changes || {} : null,
    skipped,
    previous,
    companies
  });
  return null;
};

/** Typewriter placeholder: types out each example, pauses, deletes, moves on (restarts when the list changes). */
const useTypewriter = examples => {
  const [ghost, setGhost] = useState("");
  useEffect(() => {
    let pi = 0;
    let ci = 0;
    let deleting = false;
    let timer;
    const tick = () => {
      const full = examples[pi];
      if (!deleting) {
        ci++;
        setGhost(full.slice(0, ci));
        if (ci >= full.length) {
          deleting = true;
          timer = setTimeout(tick, 2200);
          return;
        }
        timer = setTimeout(tick, 34);
      } else {
        ci--;
        setGhost(full.slice(0, ci));
        if (ci <= 0) {
          deleting = false;
          pi = (pi + 1) % examples.length;
          timer = setTimeout(tick, 450);
          return;
        }
        timer = setTimeout(tick, 14);
      }
    };
    timer = setTimeout(tick, 600);
    return () => clearTimeout(timer);
  }, [examples]);
  return ghost;
};

/** The primary, centred "describe who you're looking for" console on List Builder's start page. */
export const AiSearchHero = observer(() => {
  const companies = isCompaniesTab();
  const examples = companies ? EXAMPLES.companies : EXAMPLES.contacts;
  const [prompt, setPrompt] = useState("");
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [stepIdx, setStepIdx] = useState(0);
  const ghost = useTypewriter(examples);
  const taRef = useRef(null);
  // Applying the filters leaves the start page, which unmounts this console before the request settles.
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  // Advance the status caption while building (stops on the last one).
  useEffect(() => {
    if (!loading) {
      setStepIdx(0);
      return undefined;
    }
    const t = setInterval(() => setStepIdx(i => Math.min(i + 1, BUILD_STEPS.length - 1)), 1600);
    return () => clearInterval(t);
  }, [loading]);

  const submit = async text => {
    const p = (text ?? prompt).trim();
    if (!p || loading) return;
    setPrompt(p);
    setLoading(true);
    setErr("");
    const error = await runAiSearch(p);
    if (!mounted.current) return;
    setLoading(false);
    if (error) setErr(error);
  };

  return (
    <div className="ag-hero lbAiHero">
      <div className="ag-hero__glow" aria-hidden="true" />
      <div className="ag-hero__eyebrow">✦ AI search</div>
      <h1 className="ag-hero__title">
        {companies ? "Describe the companies you want." : "Describe who you're looking for."}{" "}
        <span className="ag-grad">We'll set the filters.</span>
      </h1>
      <p className="ag-hero__sub">
        Search 800M+ contacts and 70M+ companies in plain English — we'll turn it into filters you can refine.
      </p>

      <div className="ag-console">
        <div className={`ag-console__ring ${loading ? "is-loading" : ""} ${focused ? "is-focused" : ""}`}>
          {loading ? (
            <div className="ag-genbuild">
              <div className="ag-genbuild__orb">
                <span />
                <span />
                <span />
              </div>
              <div className="ag-genbuild__msg" key={stepIdx}>
                {BUILD_STEPS[stepIdx]}
              </div>
              <div className="ag-genbuild__quote">“{prompt.length > 90 ? prompt.slice(0, 90) + "…" : prompt}”</div>
            </div>
          ) : (
            <div className="ag-console__field">
              {!prompt && !focused && (
                <div className="ag-console__ghost" aria-hidden="true">
                  {ghost}
                  <span className="ag-console__caret" />
                </div>
              )}
              <textarea
                ref={taRef}
                className="ag-console__input"
                rows={2}
                maxLength={MAX_LENGTH}
                value={prompt}
                aria-label={companies ? "Describe the companies you want" : "Describe who you're looking for"}
                onChange={e => setPrompt(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onKeyDown={e => {
                  // Enter builds; Shift+Enter adds a line.
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
              />
              <button className="ag-console__go" onClick={() => submit()} disabled={!prompt.trim()} title="Build filters (↵)">
                <span className="ag-console__spark">✦</span> Build filters
              </button>
            </div>
          )}
        </div>

        {!loading && (
          <div className="ag-console__chips">
            {examples.map(ex => (
              <button key={ex} type="button" className="ag-chip" onClick={() => submit(ex)}>
                {ex}
              </button>
            ))}
          </div>
        )}
        {err && <div className="ag-console__err">{err}</div>}
      </div>
      <div className="lbAiHero__or">or use the filters on the left</div>
    </div>
  );
});

/** In the tab row once results are on screen: change the current filters in plain language ("only in California"). */
export const AiSearchBox = observer(() => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const companies = isCompaniesTab();
  if (dataSearchState.isOnStartPage(companies)) return null;

  const submit = async () => {
    const text = query.trim();
    if (!text || loading) return;
    setLoading(true);
    const error = await runAiSearch(text, { refine: true });
    setLoading(false);
    if (error) dataSearchState.setAiSearchNote({ error });
    else setQuery("");
  };

  return (
    <div className={`dsAiSearch${loading ? " dsAiSearch--loading" : ""}`}>
      <SparkleIcon className="dsAiSearch__icon" />
      <input
        type="text"
        className="dsAiSearch__input"
        value={query}
        maxLength={MAX_LENGTH}
        disabled={loading}
        placeholder="Refine these results, e.g. only in California, add directors, exclude agencies"
        onChange={e => setQuery(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter") submit();
        }}
      />
      <button type="button" className="dsAiSearch__btn" onClick={submit} disabled={loading || !query.trim()}>
        {loading ? "Updating…" : "Refine"}
      </button>
    </div>
  );
});

const NOTE_TIMEOUT_MS = 10000;

/** One line per change, as {kind, text}: kind drives the +/−/↻ marker. */
const noteLines = note => {
  if (note.changes) {
    const c = note.changes;
    return [
      ...(c.added || []).map(text => ({ kind: "add", text })),
      ...(c.replaced || []).map(text => ({ kind: "change", text })),
      ...(c.removed || []).map(text => ({ kind: "remove", text })),
      ...(c.cleared || []).map(text => ({ kind: "remove", text: `${text} cleared` }))
    ];
  }
  return (note.summary || []).map(item => ({ kind: "add", text: `${item.label}: ${(item.values || []).join(", ")}` }));
};

const MARKERS = { add: "+", remove: "−", change: "↻" };

/**
 * What the last plain-language search set or changed (or why it couldn't): a small card under the search box, with
 * undo. Closes itself after a few seconds (paused while hovered), and when the filters are edited by hand.
 */
export const AiSearchNote = observer(() => {
  const note = dataSearchState.aiSearchNote;
  const [hovered, setHovered] = useState(false);
  // A card closed under the cursor (×, Undo) never gets mouseleave — don't let that pause the next card.
  const noteId = note?.id;
  const [cardId, setCardId] = useState(noteId);
  if (noteId !== cardId) {
    setCardId(noteId);
    setHovered(false);
  }

  useEffect(() => {
    if (noteId == null || hovered) return undefined;
    const t = setTimeout(() => dataSearchState.setAiSearchNote(null), NOTE_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [noteId, hovered]);

  if (!note) return null;
  const close = () => dataSearchState.setAiSearchNote(null);

  const undo = async () => {
    const { filterComponentData, selectedFilteredList } = note.previous || {};
    close();
    await dataSearchState.applyFiltersFromCompanyExtensionImport(filterComponentData, selectedFilteredList, {
      skipPeopleSearch: note.companies
    });
    if (note.companies) await dataSearchState.searchCompanyData();
  };

  const lines = note.error ? [] : noteLines(note);
  const title = note.error
    ? "Couldn't update filters"
    : note.changes
    ? lines.length
      ? "Filters updated"
      : "No filters changed"
    : "Filters set from your description";

  return (
    <div
      className={`dsAiCard${note.error ? " dsAiCard--error" : ""}`}
      role="status"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="dsAiCard__head">
        <SparkleIcon className="dsAiCard__icon" />
        <span className="dsAiCard__title">{title}</span>
        <button type="button" className="dsAiCard__close" onClick={close} aria-label="Dismiss">
          ×
        </button>
      </div>
      {note.error && <div className="dsAiCard__error">{note.error}</div>}
      {lines.length > 0 && (
        <ul className="dsAiCard__lines">
          {lines.map((line, i) => (
            <li key={i} className={`dsAiCard__line dsAiCard__line--${line.kind}`}>
              <span className="dsAiCard__marker">{MARKERS[line.kind]}</span>
              <span className="dsAiCard__text">{line.text}</span>
            </li>
          ))}
        </ul>
      )}
      {note.skipped?.length > 0 && <div className="dsAiCard__skipped">Couldn't use: {note.skipped.join(", ")}</div>}
      {!note.error && (
        <div className="dsAiCard__foot">
          <button type="button" className="dsAiCard__undo" onClick={undo}>
            Undo
          </button>
          {!hovered && <span className="dsAiCard__timer" key={note.id} />}
        </div>
      )}
    </div>
  );
});

export default AiSearchBox;
